import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

type VersionEventDetail = {
  version: { timestamp: number; buildDate: string; hash: string };
  activate?: () => Promise<void> | void;
};

export default function VersionUpdateBanner() {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState<VersionEventDetail | null>(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<VersionEventDetail>;
      setDetail(ce.detail ?? null);
      setVisible(true);
      try { sessionStorage.setItem('ogs_version_update_banner_shown', '1'); } catch {}
      try { (window as any).__ogs_telemetry && (window as any).__ogs_telemetry('version_update_banner_shown', { hash: ce.detail?.version?.hash }); } catch {}
    };

    window.addEventListener('ogs:version_update_available', handler as EventListener);
    return () => window.removeEventListener('ogs:version_update_available', handler as EventListener);
  }, []);

  if (!visible || !detail) return null;

  return (
    <div className={cn('fixed left-1/2 -translate-x-1/2 bottom-6 z-60 w-[min(980px,92vw)] rounded-xl shadow-lg bg-card/95 backdrop-blur-md p-3 flex items-center justify-between gap-4')}
         role="status" aria-live="polite">
      <div className="flex items-center gap-3">
        <div className="text-sm font-semibold">A new version is available</div>
        <div className="text-sm text-muted-foreground">Install the latest fixes and performance improvements.</div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => { setVisible(false); try { (window as any).__ogs_telemetry && (window as any).__ogs_telemetry('version_update_dismissed', { hash: detail?.version?.hash }); } catch {} }}>Dismiss</Button>
        <Button onClick={async () => {
          setWorking(true);
          try {
            // show a non-blocking toast while activating
            toast({ title: 'Updating...', description: 'Applying update in background', duration: 3000 });
            await detail.activate?.();
            setVisible(false);
            try { (window as any).__ogs_telemetry && (window as any).__ogs_telemetry('version_update_accepted', { hash: detail.version.hash }); } catch {}
          } catch (e) {
            setVisible(false);
            toast({ title: 'Update failed', description: 'Could not apply update automatically; reloading page', duration: 4000 });
            try { (window as any).__ogs_telemetry && (window as any).__ogs_telemetry('version_update_activation_failed', { error: String(e) }); } catch {}
            try { window.location.reload(); } catch {}
          } finally {
            setWorking(false);
          }
        }} disabled={working}>Update</Button>
      </div>
    </div>
  );
}
